import { useQuery } from '@tanstack/react-query';
import Decimal from 'decimal.js';

import { AcChartValue } from '../components/AcChartBox/AcChartBox.types';
import { ELocalStorageKeys, EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';
import { localStorageUtil } from '../utils/localStorageUtil';
import {
  BaseBreakdownValue,
  BaseStatisticValue,
  EStatisticType
} from '../views/Statistics/StatisticsView.types';

import { fetchConfig } from './api.utils';

export enum EMetric {
  TOTAL_REVENUE = 'totalRevenue',
  DEPOSITS = 'deposits',
  ARPPU = 'arppu',
  AVERAGE_DEPOSITS_PER_DEPOSITOR = 'averageDepositsPerDepositor',
  DEPOSITORS = 'depositors',
  ARPU = 'arpu',
  ACTIVE_USERS = 'activeUsers',
  SESSIONS = 'sessions',
  CONVERSION_RATE = 'conversionRate',
  DAU = 'dau',
  TOTAL_REVENUE_WITHOUT_TAX = 'totalRevenueWithoutTax',
  DEPOSITS_PER_PRICE_POINT = 'depositsPerPricePoint',
  REVENUE_PER_PRICE_POINT = 'revenuePerPricePoint',
  REFUND_AMOUNT_RATE = 'refundAmountRate',
  REFUND_RATE = 'refundRate',
  DISPUTES_AMOUNT_RATE = 'disputesAmountRate',
  DISPUTES_LOST_COUNT = 'disputesLostCount',
  DISPUTES_WIN_COUNT = 'disputesWinCount',
  DISPUTES_IN_PROGRESS_COUNT = 'disputesInProgressCount',
  DISPUTES_RATE = 'disputesRate',
  DISPUTES_WIN_RATE = 'disputeWinRate',
  PAYMENT_METHODS_DISTRIBUTION = 'paymentMethodsDistribution',
  PAYMENT_SUCCESS_ORDER_RATE = 'paymentSuccessOrderRate',
  PAYMENT_FAILURE_ORDER_RATE = 'paymentFailureOrderRate'
}

export enum EMetricType {
  PRICE = 'price',
  AMOUNT = 'amount'
}

interface MetricMetadata {
  title: string;
  description?: string;
  type?: EStatisticType;
}

export const metricDictionary: Record<EMetric, MetricMetadata> = {
  [EMetric.TOTAL_REVENUE]: {
    title: 'Total Revenue',
    type: EStatisticType.CURRENCY,
    description: 'Total charged income.'
  },
  [EMetric.TOTAL_REVENUE_WITHOUT_TAX]: {
    title: 'Total Revenue Without Tax',
    type: EStatisticType.CURRENCY,
    description: 'Total charged income excluding tax.'
  },
  [EMetric.DEPOSITS]: {
    title: 'Total Deposits',
    type: EStatisticType.NUMBER,
    description: 'Number of deposits made.'
  },
  [EMetric.ARPPU]: {
    title: 'ARPPU',
    type: EStatisticType.AMOUNT_AVERAGE,
    description: 'Total revenue divided by unique depositors.'
  },
  [EMetric.AVERAGE_DEPOSITS_PER_DEPOSITOR]: {
    title: 'Average Deposits Per Depositor',
    type: EStatisticType.PRECISE_NUMBER,
    description: 'Total deposits divided by total depositors.'
  },
  [EMetric.DEPOSITORS]: {
    title: 'Depositors',
    type: EStatisticType.NUMBER,
    description: 'Total number of unique depositors.'
  },
  [EMetric.ARPU]: {
    title: 'ARPU',
    type: EStatisticType.AMOUNT_AVERAGE,
    description: 'Total revenue divided by active users.'
  },
  [EMetric.ACTIVE_USERS]: {
    title: 'Active Users',
    type: EStatisticType.NUMBER,
    description: 'Users who performed an active action.'
  },
  [EMetric.SESSIONS]: {
    title: 'Total Sessions',
    type: EStatisticType.NUMBER,
    description: 'Total number of sessions.'
  },
  [EMetric.CONVERSION_RATE]: {
    title: 'Conversion Rate',
    type: EStatisticType.PERCENTAGE,
    description: 'Number of depositors divided by number of active users.'
  },
  [EMetric.DAU]: {
    title: 'Average Daily Active Users',
    type: EStatisticType.NUMBER,
    description: 'Average number of active users per day.'
  },
  [EMetric.DEPOSITS_PER_PRICE_POINT]: {
    title: 'Deposits Per Price Point',
    type: EStatisticType.NUMBER,
    description: 'Number of deposits per price point.'
  },
  [EMetric.REVENUE_PER_PRICE_POINT]: {
    title: 'Revenue Per Price Point',
    type: EStatisticType.CURRENCY,
    description: 'Revenue per price point.'
  },
  [EMetric.PAYMENT_METHODS_DISTRIBUTION]: {
    title: 'Payment Method Distribution',
    type: EStatisticType.PERCENTAGE,
    description: 'Breakdown of transactions by payment method used.'
  },
  [EMetric.PAYMENT_SUCCESS_ORDER_RATE]: {
    title: 'Order Success Rate',
    type: EStatisticType.PERCENTAGE,
    description:
      'The percentage of successful orders out of all payment attempts.'
  },
  [EMetric.PAYMENT_FAILURE_ORDER_RATE]: {
    title: 'Decline Rate',
    type: EStatisticType.PERCENTAGE,
    description:
      'The percentage of declined payments out of all payment attempts.'
  },
  [EMetric.REFUND_AMOUNT_RATE]: {
    title: 'Refund Amount Rate',
    type: EStatisticType.PERCENTAGE,
    description: 'The percentage of total revenue that was refunded.'
  },
  [EMetric.REFUND_RATE]: {
    title: 'Refund Rate',
    type: EStatisticType.PERCENTAGE,
    description: 'The percentage of completed orders that were refunded.'
  },
  [EMetric.DISPUTES_RATE]: {
    title: 'Chargeback Rate',
    type: EStatisticType.PERCENTAGE,
    description:
      'The percentage of completed orders that resulted in a chargeback.'
  },
  [EMetric.DISPUTES_AMOUNT_RATE]: {
    title: 'Chargeback Amount Rate',
    type: EStatisticType.PERCENTAGE,
    description:
      'The percentage of total revenue that was lost due to chargebacks.'
  },
  [EMetric.DISPUTES_WIN_RATE]: {
    title: 'Chargeback Win Rate',
    type: EStatisticType.PERCENTAGE,
    description:
      'The percentage of chargebacks that were successfully disputed and won.'
  },
  [EMetric.DISPUTES_WIN_COUNT]: {
    title: 'Chargeback Cases: Won',
    type: EStatisticType.NUMBER,
    description: 'The number of chargebacks that were successfully won.'
  },
  [EMetric.DISPUTES_LOST_COUNT]: {
    title: 'Chargeback Cases: Lost',
    type: EStatisticType.NUMBER,
    description:
      'The number of chargebacks that were lost after dispute resolution.'
  },
  [EMetric.DISPUTES_IN_PROGRESS_COUNT]: {
    title: 'Chargeback Cases: In Progress',
    type: EStatisticType.NUMBER,
    description:
      'The number of chargebacks that are still under review or awaiting resolution.'
  }
};

export interface StatsResult {
  name: EMetric;
  type: EStatisticType;
  currentValue?: number;
  previousValue?: number;
  changePercentage?: number;
}

export interface TrendsResult {
  name: EMetric;
  type: EStatisticType;
  values: BaseStatisticValue[];
}

export interface BreakdownsResult {
  name: EMetric;
  type: EStatisticType;
  values: BaseBreakdownValue[];
}

export default function useAnalytics(
  startDateTime: string | undefined,
  endDateTime: string | undefined,
  statsMetrics: EMetric[],
  trendsMetrics: EMetric[],
  breakdownsMetrics: EMetric[],
  currentPublisherId?: string,
  attributeFilters?: Record<string, string[]>
) {
  const axios = useAxios();

  const getAnalytics = useQuery({
    ...fetchConfig.general,
    queryKey: [
      `${EQueryKeys.ANALYTICS}_${startDateTime}_${endDateTime}_${statsMetrics}_${JSON.stringify(attributeFilters || {})}`
    ],
    queryFn: async () => {
      if (!startDateTime || !endDateTime) {
        return [];
      }
      let data = await axios.get(
        '/dashboard/analytics/stats',
        {
          startDate: startDateTime,
          endDate: endDateTime,
          metrics: statsMetrics,
          ...(attributeFilters && { attributeFilters })
        },
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      if (localStorageUtil.get(ELocalStorageKeys.OVERRIDE_STATS)) {
        data = JSON.parse(
          localStorageUtil.get(ELocalStorageKeys.OVERRIDE_STATS)
        );
      }
      const result: StatsResult[] = data.stats.map((el: any) => {
        if (
          el.type === EStatisticType.CURRENCY &&
          el.name !== EMetric.ARPPU &&
          el.name !== EMetric.ARPU
        ) {
          return {
            ...el,
            currentValue: el ? new Decimal(el.currentValue || 0).div(100) : 0
          };
        }
        return el;
      });

      return result;
    },
    enabled: !!currentPublisherId
  });

  const getTrends = useQuery({
    ...fetchConfig.general,
    queryKey: [
      `${EQueryKeys.TRENDS}_${startDateTime}_${endDateTime}_${trendsMetrics}_${JSON.stringify(attributeFilters || {})}`
    ],
    queryFn: async () => {
      if (!startDateTime || !endDateTime) {
        return [];
      }
      let data = await axios.get(
        '/dashboard/analytics/trends',
        {
          startDate: startDateTime,
          endDate: endDateTime,
          metrics: trendsMetrics,
          ...(attributeFilters && { attributeFilters })
        },
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );

      if (localStorageUtil.get(ELocalStorageKeys.OVERRIDE_TRENDS)) {
        data = JSON.parse(
          localStorageUtil.get(ELocalStorageKeys.OVERRIDE_TRENDS)
        );
      }

      const result: TrendsResult[] = data.trends.map((el: any) => {
        if (
          el.type === EStatisticType.CURRENCY ||
          el.name === EMetric.ARPPU ||
          el.name === EMetric.ARPU
        ) {
          return {
            ...el,
            type:
              el.name === EMetric.ARPPU || el.name === EMetric.ARPU
                ? EStatisticType.CURRENCY
                : el.type,
            values: el.values.map((v: AcChartValue) => {
              return {
                ...v,
                value: new Decimal(v.value || 0).div(100)
              };
            })
          };
        }
        return el;
      });

      return result;
    },
    enabled: !!currentPublisherId
  });

  const getBreakdowns = useQuery({
    ...fetchConfig.general,
    queryKey: [
      `${EQueryKeys.ANALYTICS}_${startDateTime}_${endDateTime}_${breakdownsMetrics}_${JSON.stringify(attributeFilters || {})}`
    ],
    queryFn: async () => {
      if (!startDateTime || !endDateTime) {
        return [];
      }
      let data = await axios.get(
        '/dashboard/analytics/breakdowns',
        {
          startDate: startDateTime,
          endDate: endDateTime,
          metrics: breakdownsMetrics,
          ...(attributeFilters && { attributeFilters })
        },
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      if (localStorageUtil.get(ELocalStorageKeys.OVERRIDE_STATS)) {
        data = JSON.parse(
          localStorageUtil.get(ELocalStorageKeys.OVERRIDE_STATS)
        );
      }
      const result: BreakdownsResult[] = data.breakdowns.map((el: any) => {
        const isPriceBreakdown =
          el.name === EMetric.DEPOSITS_PER_PRICE_POINT ||
          el.name === EMetric.REVENUE_PER_PRICE_POINT;
        const isPriceMetric = el.type === EStatisticType.CURRENCY;
        if (isPriceMetric || isPriceBreakdown) {
          return {
            ...el,
            values: el.values.map((v: BaseBreakdownValue) => {
              return {
                breakdownValue: isPriceBreakdown
                  ? `$${(v.breakdownValue as number) / 100}`
                  : v.breakdownValue,
                value: isPriceMetric
                  ? new Decimal(v.value || 0).div(100)
                  : v.value
              };
            })
          };
        }
        return el;
      });
      return result;
    },
    enabled: !!currentPublisherId
  });

  return {
    getAnalytics,
    getTrends,
    getBreakdowns
  };
}
